import { useNavigate } from 'react-router-dom';

export default function MoringaProductHero() {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-stone-50">
      {/* Background texture */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/59212ca02aa22586a87464a3609ca7ce.jpg)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-10">
          <button onClick={() => navigate('/')} className="hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <button onClick={() => navigate('/products')} className="hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap">Products</button>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-gray-700 font-medium">Pure Moringa Powder</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — copy */}
          <div>
            {/* Badges row */}
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="inline-flex items-center bg-green-100 text-green-700 text-xs font-bold px-3 py-1.5 rounded-full border border-green-200 uppercase tracking-wider">
                <i className="ri-checkbox-circle-fill mr-1.5 text-sm"></i>100% Pure
              </span>
              <span className="inline-flex items-center bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full border border-amber-200 uppercase tracking-wider">
                <i className="ri-map-pin-fill mr-1.5 text-sm"></i>Grown in Ghana
              </span>
              <span className="inline-flex items-center bg-stone-100 text-stone-700 text-xs font-bold px-3 py-1.5 rounded-full border border-stone-200 uppercase tracking-wider">
                <i className="ri-leaf-fill mr-1.5 text-sm"></i>No Additives
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5">
              Pure<br />
              <span className="text-green-600">Moringa</span><br />
              Powder
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-lg">
              One ingredient. A thousand benefits. Our moringa is slow-dried and milled at low heat to preserve every vitamin, mineral, and antioxidant — exactly as nature intended.
            </p>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-4 mb-10">
              {[
                { value: '7×', label: 'More Vit C\nthan oranges' },
                { value: '4×', label: 'More calcium\nthan milk' },
                { value: '3×', label: 'More iron\nthan spinach' },
              ].map((stat, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                  <div className="text-3xl font-extrabold text-green-600">{stat.value}</div>
                  <div className="text-xs text-gray-500 mt-1 leading-snug whitespace-pre-line">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Star rating */}
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-400 text-lg"></i>
                  </span>
                ))}
              </div>
              <span className="text-gray-900 font-semibold text-sm">5.0</span>
              <span className="text-gray-400 text-sm">· 6 verified buyers in Ghana</span>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <a
                href="https://paystack.shop/pay/v9-4hu3n50"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-shopping-bag-line mr-2 text-lg"></i>Order Now via Paystack
              </a>
              <button
                onClick={() => navigate('/contact')}
                className="inline-flex items-center border-2 border-gray-300 text-gray-700 hover:border-green-500 hover:text-green-600 font-semibold px-7 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-chat-3-line mr-2"></i>Ask a Question
              </button>
            </div>
          </div>

          {/* Right — product image */}
          <div className="relative">
            {/* Decorative green circle background */}
            <div className="absolute inset-0 rounded-full bg-green-100 scale-75 translate-x-8 opacity-60" />

            <div className="relative w-full aspect-square max-w-md mx-auto rounded-3xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_e17d2c62eadfd03030c4db97e8f52ea6_45dfadd7.jpg"
                alt="Pure Moringa Powder — MY AVA Farms Ghana"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Floating info card */}
            <div className="absolute bottom-4 left-0 bg-white border border-gray-200 rounded-2xl px-5 py-4 flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                <i className="ri-leaf-line text-green-600 text-xl"></i>
              </span>
              <div>
                <p className="text-xs text-gray-400 font-medium">Available sizes</p>
                <p className="text-sm font-bold text-gray-900">50g · 100g · 250g · 1kg</p>
              </div>
            </div>

            {/* Top-right verified badge */}
            <div className="absolute top-4 right-0 bg-white border border-green-200 rounded-2xl px-5 py-3 flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center bg-green-600 rounded-full">
                <i className="ri-shield-check-fill text-white text-sm"></i>
              </span>
              <div>
                <p className="text-xs text-green-700 font-bold">Lab Verified</p>
                <p className="text-xs text-gray-400">No additives</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
